import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ImagePlus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { PackageForm, PackageAdminList } from "@/PackageAdmin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const EQUIPMENT = [
  { key: "speakers", label: "Speakers" },
  { key: "stage", label: "Stage" },
  { key: "lighting", label: "Lighting" },
  { key: "mic", label: "Microphones" },
  { key: "stand", label: "Stands" },
  { key: "mixer", label: "Mixers" },
  { key: "generator", label: "Generators" },
  { key: "fans", label: "Fans" },
  { key: "marquee", label: "Marquee" },
  { key: "cables", label: "Cables" },
];

function AdminGalleryImagePreview({ img, onDelete, onUpdateCaption }) {
  const [editing, setEditing] = useState(false);
  const [captionVal, setCaptionVal] = useState(img.caption || '');
  const [editingId, setEditingId] = useState(false);
  const [idVal, setIdVal] = useState(img.display_id || '');

  const handleSaveCaption = async () => {
    await onUpdateCaption(img.id, { caption: captionVal });
    setEditing(false);
  };

  const handleSaveId = async () => {
    await onUpdateCaption(img.id, { display_id: idVal });
    setEditingId(false);
  };

  return (
    <div className="image-preview-item" style={{ position: 'relative' }}>
      <img src={getImageUrl(img.image_url)} alt={img.caption || 'Gallery image'} />
      {editingId ? (
        <div style={{ position: 'absolute', top: '6px', left: '6px', display: 'flex', gap: '2px' }}>
          <Input type="text" value={idVal} onChange={(e) => setIdVal(e.target.value)} style={{ width: '80px', fontSize: '0.7rem', padding: '2px 6px', height: 'auto', borderRadius: '6px' }} data-testid={`edit-id-${img.id}`} />
          <Button size="sm" onClick={handleSaveId} style={{ fontSize: '0.65rem', padding: '2px 6px', height: 'auto', borderRadius: '6px' }} data-testid={`save-id-${img.id}`}>OK</Button>
        </div>
      ) : (
        <div onClick={() => setEditingId(true)} style={{ position: 'absolute', top: '6px', left: '6px', background: 'rgba(15,81,50,0.85)', color: 'white', padding: '2px 8px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700, fontFamily: "'Space Grotesk', monospace", cursor: 'pointer' }} title="Click to rename" data-testid={`display-id-${img.id}`}>{img.display_id || '—'}</div>
      )}
      <button onClick={() => onDelete(img.id)} aria-label="Delete image" data-testid={`delete-${img.id}`}>&times;</button>
      <div style={{ padding: '6px 8px', background: 'white', borderTop: '1px solid var(--border-color)' }}>
        {editing ? (
          <div style={{ display: 'flex', gap: '4px' }}>
            <Input type="text" value={captionVal} onChange={(e) => setCaptionVal(e.target.value)} style={{ fontSize: '0.75rem', padding: '4px 8px', height: 'auto' }} data-testid={`edit-caption-${img.id}`} />
            <Button size="sm" onClick={handleSaveCaption} style={{ fontSize: '0.7rem', padding: '4px 8px', height: 'auto' }} data-testid={`save-caption-${img.id}`}>Save</Button>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setEditing(true)} data-testid={`caption-area-${img.id}`}>
            <span style={{ fontSize: '0.75rem', color: img.caption ? 'var(--text-muted)' : 'var(--text-light)', fontStyle: img.caption ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img.caption || 'Click to add caption'}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function GalleryUploadForm({ section, onUpload, onFileUpload }) {
  const [imageUrl, setImageUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [uploadMode, setUploadMode] = useState('file');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUrlSubmit = async (e) => {
    e.preventDefault();
    if (!imageUrl.trim()) { toast.error('Please enter an image URL'); return; }
    try { await onUpload(section, imageUrl, caption); setImageUrl(''); setCaption(''); toast.success('Image added!'); }
    catch { toast.error('Failed to add image'); }
  };

  const handleFileSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) { toast.error('Please select a file'); return; }
    setIsUploading(true);
    try {
      await onFileUpload(section, selectedFile, caption);
      setSelectedFile(null); setCaption('');
      const fileInput = document.getElementById(`${section}-file-input`);
      if (fileInput) fileInput.value = '';
      toast.success('Image uploaded!');
    } catch { toast.error('Failed to upload image'); }
    finally { setIsUploading(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
        <Button type="button" variant={uploadMode === 'file' ? 'default' : 'outline'} onClick={() => setUploadMode('file')} size="sm">Upload File</Button>
        <Button type="button" variant={uploadMode === 'url' ? 'default' : 'outline'} onClick={() => setUploadMode('url')} size="sm">Use URL</Button>
      </div>
      {uploadMode === 'file' ? (
        <form onSubmit={handleFileSubmit} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: '180px' }}><Label htmlFor={`${section}-file-input`} style={{ marginBottom: '4px', display: 'block' }}>Select Image</Label><Input id={`${section}-file-input`} type="file" accept="image/*" onChange={(e) => setSelectedFile(e.target.files[0])} data-testid={`${section}-file-input`} /></div>
          <div style={{ flex: 1, minWidth: '180px' }}><Label htmlFor={`${section}-file-caption`} style={{ marginBottom: '4px', display: 'block' }}>Caption</Label><Input id={`${section}-file-caption`} type="text" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Optional" data-testid={`${section}-file-caption`} /></div>
          <Button type="submit" className="btn-primary" disabled={isUploading} data-testid={`${section}-upload-btn`}><ImagePlus className="mr-2" size={16} />{isUploading ? 'Uploading...' : 'Upload'}</Button>
        </form>
      ) : (
        <form onSubmit={handleUrlSubmit} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '180px' }}><Input type="url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="Image URL" data-testid={`${section}-url-input-admin`} /></div>
          <div style={{ flex: 1, minWidth: '180px' }}><Input type="text" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" data-testid={`${section}-caption-input-admin`} /></div>
          <Button type="submit" className="btn-primary" data-testid={`${section}-add-btn-admin`}><ImagePlus className="mr-2" size={16} />Add</Button>
        </form>
      )}
    </div>
  );
}

function AdminGallerySection({ title, section, images, onUpload, onFileUpload, onDelete, onUpdateCaption }) {
  return (
    <Card style={{ marginBottom: '20px' }}>
      <CardHeader><CardTitle style={{ textTransform: 'capitalize' }}>{title} ({images.length} images)</CardTitle></CardHeader>
      <CardContent>
        <GalleryUploadForm section={section} onUpload={onUpload} onFileUpload={onFileUpload} />
        {images.length > 0 && (
          <div className="image-preview-grid" style={{ marginTop: '20px' }}>
            {images.map((img) => <AdminGalleryImagePreview key={img.id} img={img} onDelete={onDelete} onUpdateCaption={onUpdateCaption} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ContactMessageCard({ msg }) {
  return (
    <Card>
      <CardContent style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}><strong>{msg.name}</strong><span style={{ color: 'var(--text-light)', fontSize: '0.8rem' }}>{new Date(msg.created_at).toLocaleDateString()}</span></div>
        <p style={{ color: 'var(--text-muted)', marginBottom: '6px', fontSize: '0.9rem' }}>{msg.email}</p>
        <p style={{ margin: 0 }}>{msg.message}</p>
      </CardContent>
    </Card>
  );
}

export default function AdminPage() {
  const [isAdmin, setIsAdmin] = useState(() => localStorage.getItem('ev_admin') === 'true');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [galleries, setGalleries] = useState({});
  const [messages, setMessages] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editingPackage, setEditingPackage] = useState(null);
  const [showPackageForm, setShowPackageForm] = useState(false);

  const fetchData = async () => {
    try {
      const [galleriesRes, messagesRes, packagesRes] = await Promise.all([
        axios.get(`${API}/galleries`), axios.get(`${API}/contact`), axios.get(`${API}/packages`)
      ]);
      setGalleries(galleriesRes.data);
      setMessages(messagesRes.data || []);
      setPackages(packagesRes.data || []);
    } catch (error) { console.error('Failed to fetch data:', error); }
  };

  useEffect(() => { if (isAdmin) fetchData(); }, [isAdmin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.success) { localStorage.setItem('ev_admin', 'true'); setIsAdmin(true); setError(''); toast.success('Logged in!'); }
    } catch { setError('Incorrect password'); }
  };

  const logout = () => { localStorage.removeItem('ev_admin'); setIsAdmin(false); };

  const handleUploadImage = async (section, imageUrl, caption) => {
    await axios.post(`${API}/galleries`, { section, image_url: imageUrl, caption });
    fetchData();
  };
  const handleFileUpload = async (section, file, caption) => {
    const formData = new FormData();
    formData.append('section', section);
    formData.append('file', file);
    if (caption) formData.append('caption', caption);
    await axios.post(`${API}/galleries/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    fetchData();
  };
  const handleDeleteImage = async (imageId) => {
    if (window.confirm('Delete this image?')) {
      await axios.delete(`${API}/galleries/${imageId}`);
      toast.success('Image deleted');
      fetchData();
    }
  };
  const handleUpdateCaption = async (imageId, updateData) => {
    try { await axios.patch(`${API}/galleries/${imageId}`, updateData); toast.success('Updated successfully'); fetchData(); }
    catch { toast.error('Failed to update'); }
  };
  const handleSavePackage = async (pkgData) => {
    try {
      if (editingPackage) { await axios.put(`${API}/packages/${editingPackage.id}`, pkgData); toast.success('Package updated'); }
      else { await axios.post(`${API}/packages`, pkgData); toast.success('Package created'); }
      setEditingPackage(null); setShowPackageForm(false); fetchData();
    } catch { toast.error('Failed to save package'); }
  };
  const handleDeletePackage = async (pkgId) => {
    if (window.confirm('Delete this package?')) {
      await axios.delete(`${API}/packages/${pkgId}`);
      toast.success('Package deleted'); fetchData();
    }
  };

  if (!isAdmin) {
    return (
      <div className="admin-container">
        <div className="admin-card">
          <h1 style={{ fontSize: '1.75rem', marginBottom: '6px', color: 'var(--primary)' }} data-testid="admin-title"><Lock className="inline mr-2" size={24} />Admin Access</h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: '28px', fontSize: '0.95rem' }}>Enter the admin password to manage your site</p>
          <form onSubmit={handleLogin}>
            <div className="form-group"><Label htmlFor="password" className="form-label">Password</Label><Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="form-input" placeholder="Enter admin password" data-testid="admin-password-input" /></div>
            {error && <p style={{ color: 'var(--error)', marginBottom: '14px', fontSize: '0.9rem' }} data-testid="admin-error">{error}</p>}
            <Button type="submit" className="btn-primary" data-testid="admin-login-btn">Login</Button>
          </form>
          <div style={{ marginTop: '28px', paddingTop: '20px', borderTop: '1px solid var(--border-color)' }}><Link to="/" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem' }} data-testid="admin-back-link">&larr; Back to Site</Link></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '36px' }}>
          <div>
            <h1 style={{ fontSize: '1.75rem', color: 'var(--primary)', marginBottom: '4px' }} data-testid="admin-dashboard-title">Admin Dashboard</h1>
            <Link to="/" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.9rem' }}>&larr; Back to Site</Link>
          </div>
          <Button onClick={logout} variant="outline" data-testid="admin-logout-btn">Logout</Button>
        </div>
        <div style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '1.3rem', marginBottom: '20px', color: 'var(--text-main)' }}>Gallery Management</h2>
          {EQUIPMENT.map((eq) => (
            <AdminGallerySection key={eq.key} title={`${eq.label} Gallery`} section={eq.key} images={galleries[eq.key] || []} onUpload={handleUploadImage} onFileUpload={handleFileUpload} onDelete={handleDeleteImage} onUpdateCaption={handleUpdateCaption} />
          ))}
        </div>
        <div style={{ marginBottom: '40px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '1.3rem', color: 'var(--text-main)', margin: 0 }}>Package Management ({packages.length})</h2>
            <Button onClick={() => { setEditingPackage(null); setShowPackageForm(true); }} className="btn-primary" data-testid="add-package-btn"><ImagePlus className="mr-2" size={16} />Add Package</Button>
          </div>
          {showPackageForm && <PackageForm initial={editingPackage} onSave={handleSavePackage} onCancel={() => { setShowPackageForm(false); setEditingPackage(null); }} EQUIPMENT={EQUIPMENT} galleries={galleries} />}
          {packages.length === 0 && !showPackageForm ? (
            <Card><CardContent style={{ padding: '36px', textAlign: 'center', color: 'var(--text-muted)' }}>No packages yet. Click "Add Package" to create one.</CardContent></Card>
          ) : (
            <PackageAdminList packages={packages} onEdit={(pkg) => { setEditingPackage(pkg); setShowPackageForm(true); }} onDelete={handleDeletePackage} />
          )}
        </div>
        <div>
          <h2 style={{ fontSize: '1.3rem', marginBottom: '20px', color: 'var(--text-main)' }}>Contact Messages ({messages.length})</h2>
          {messages.length === 0 ? (
            <Card><CardContent style={{ padding: '36px', textAlign: 'center', color: 'var(--text-muted)' }}>No messages yet</CardContent></Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {messages.map((msg) => <ContactMessageCard key={msg.id} msg={msg} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
